import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/translations/TranslationContext";

const SITE = "https://algebrasolution.ca";

type Meta = { title: string; description: string };

const META: Record<string, { en: Meta; fr: Meta }> = {
  "/": {
    en: {
      title: "Accounting, Bookkeeping & Tax Services in Montreal | Algebra Solutions",
      description: "Algebra Solutions is a Pointe-Claire, Montreal accounting firm offering bookkeeping, tax preparation, financial planning and business consulting for small and mid-sized businesses."
    },
    fr: {
      title: "Comptabilité, tenue de livres et fiscalité à Montréal | Algebra Solutions",
      description: "Algebra Solutions, cabinet comptable à Pointe-Claire (Montréal) : tenue de livres, déclarations fiscales, planification financière et consultation d'affaires pour les PME."
    }
  },
  "/about": {
    en: {
      title: "About Us | Algebra Solutions Accounting Firm",
      description: "Meet the Algebra Solutions team: experienced accountants in Pointe-Claire helping Quebec businesses make confident financial decisions."
    },
    fr: {
      title: "À propos | Algebra Solutions, cabinet comptable",
      description: "Découvrez l'équipe d'Algebra Solutions : des comptables expérimentés à Pointe-Claire qui aident les entreprises québécoises à décider en confiance."
    }
  },
  "/services": {
    en: {
      title: "Accounting Services | Algebra Solutions",
      description: "Monthly bookkeeping, tax preparation, financial planning and strategic business consulting from Algebra Solutions in Montreal."
    },
    fr: {
      title: "Services comptables | Algebra Solutions",
      description: "Tenue de livres mensuelle, préparation fiscale, planification financière et soutien stratégique offerts par Algebra Solutions à Montréal."
    }
  },
  "/monthly-bookkeeping": {
    en: {
      title: "Monthly Bookkeeping Services in Montreal | Algebra Solutions",
      description: "Accurate, up-to-date books every month. Algebra Solutions handles bookkeeping, reconciliations and reporting so you can focus on your business."
    },
    fr: {
      title: "Tenue de livres mensuelle à Montréal | Algebra Solutions",
      description: "Des livres à jour et précis chaque mois. Algebra Solutions s'occupe de la tenue de livres, des conciliations et des rapports."
    }
  },
  "/tax-preparation": {
    en: {
      title: "Tax Preparation & Tax Planning in Montreal | Algebra Solutions",
      description: "Corporate and personal tax preparation and planning in Quebec. Reduce your tax burden and stay compliant with Algebra Solutions."
    },
    fr: {
      title: "Préparation fiscale et planification fiscale à Montréal | Algebra Solutions",
      description: "Préparation et planification fiscale pour entreprises et particuliers au Québec. Réduisez votre fardeau fiscal en toute conformité."
    }
  },
  "/financial-planning": {
    en: {
      title: "Financial Planning & Analysis | Algebra Solutions",
      description: "Budgeting, forecasting and financial analysis to plan growth, hiring and investment with confidence. Algebra Solutions, Montreal."
    },
    fr: {
      title: "Planification et analyse financière | Algebra Solutions",
      description: "Budgets, prévisions et analyse financière pour planifier votre croissance, vos embauches et vos investissements. Algebra Solutions, Montréal."
    }
  },
  "/business-consulting": {
    en: {
      title: "Strategic Business Consulting | Algebra Solutions",
      description: "Personalized strategic support for your business challenges and goals, from the accounting team at Algebra Solutions in Montreal."
    },
    fr: {
      title: "Consultation d'affaires stratégique | Algebra Solutions",
      description: "Un soutien stratégique personnalisé pour vos défis et objectifs d'affaires, par l'équipe comptable d'Algebra Solutions à Montréal."
    }
  },
  "/contact": {
    en: {
      title: "Contact Us | Algebra Solutions, Pointe-Claire, Montreal",
      description: "Book a consultation with Algebra Solutions. 6500 Trans-Canada Hwy, Ste 400, Pointe-Claire, QC H9R 0A5. Call (514) 508-5552."
    },
    fr: {
      title: "Nous joindre | Algebra Solutions, Pointe-Claire, Montréal",
      description: "Planifiez une consultation avec Algebra Solutions. 6500 Trans-Canada Hwy, Ste 400, Pointe-Claire, QC H9R 0A5. Tél. : (514) 508-5552."
    }
  }
};

const setMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const Seo = () => {
  const { pathname } = useLocation();
  const { language } = useTranslation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
    const lang = language === "fr" ? "fr" : "en";
    const entry = META[path];
    const known = Boolean(entry);
    const { title, description } = (entry || META["/"])[lang];
    const url = SITE + (path === "/" ? "/" : path);

    document.documentElement.lang = lang;
    document.title = title;
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[property="og:locale"]', "property", "og:locale", lang === "fr" ? "fr_CA" : "en_CA");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    setMeta('meta[name="robots"]', "name", "robots", known ? "index, follow" : "noindex, follow");

    const canonical = document.getElementById("canonical-link") as HTMLLinkElement | null;
    if (canonical) canonical.href = url;
  }, [pathname, language]);

  return null;
};

export default Seo;
